import React, { forwardRef, useState } from 'react'

import cn from 'classnames'

import styles from './BaseInput.module.scss'
import { BaseInputLabel } from './components/BaseInputLabel'
import { BaseInputMetaText } from './components/BaseInputMetaText'
import { BaseInputProps } from './types'

const BaseInput: React.FC<BaseInputProps> = forwardRef(
  (
    {
      label,
      name,
      id,
      placeholder,
      helperText,
      type = 'text',
      className,
      isRequired,
      isInvalid,
      isSuccess,
      isDisabled,
      value,
      onChange,
      beforeContent,
      inputMode = 'none',
      afterContent,
      afterContentPadding = true,
      hasDataTrackingPii,
      isReadOnly = false,
      inputClassName,
      isLabelVisible,
      isMarginless,
      testId,
      ...props
    },
    ref,
  ) => {
    const [isActive, setIsActive] = useState<boolean>(false)

    return (
      <div className={className}>
        <BaseInputLabel
          id={id}
          name={name}
          isDisabled={isDisabled}
          isRequired={isRequired}
          isLabelVisible={isLabelVisible}
        >
          {label}
        </BaseInputLabel>
        <div
          onFocus={() => {
            setIsActive(true)
          }}
          onBlur={() => {
            setIsActive(false)
          }}
          className={cn(
            styles.inputContainer,
            isMarginless && styles.inputContainer_marginless,
            isRequired && styles.inputContainer_required,
            isInvalid && styles.inputContainer_invalid,
            isSuccess && styles.inputContainer_success,
            isDisabled && styles.inputContainer_disabled,
            isActive && styles.inputContainer_active,
          )}
        >
          {beforeContent && <div className={styles.beforeContent}>{beforeContent}</div>}
          <input
            id={id || name}
            name={name}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            className={cn(inputClassName, styles.input, isDisabled && styles.input_disabled)}
            disabled={isDisabled}
            required={isRequired}
            type={type}
            aria-invalid={isInvalid}
            aria-errormessage={isInvalid ? `${name}-message` : undefined}
            inputMode={inputMode}
            data-tracking-pii={hasDataTrackingPii}
            readOnly={isReadOnly}
            data-testid={testId}
            ref={ref}
            {...props}
          />
          {afterContent && (
            <div
              className={cn(
                styles.afterContent,
                afterContentPadding && styles.afterContent_padding,
                isSuccess && styles.afterContent_success,
                isInvalid && styles.afterContent_invalid,
              )}
            >
              {afterContent}
            </div>
          )}
        </div>
        {helperText && <BaseInputMetaText isInvalid={isInvalid}>{helperText}</BaseInputMetaText>}
      </div>
    )
  },
)

export default BaseInput
